// src/hooks/useWeb3.js
import { useState, useEffect } from "react";
import { connectWallet } from "../services/web3Service";

// Use a singleton pattern to maintain a persistent connection state across components
let globalWeb3State = {
  provider: null,
  signer: null,
  contract: null,
  governanceContract: null,
  yildxTokenContract: null,
  account: null,
  isConnected: false
};

let listeners = [];

const triggerListeners = () => {
  listeners.forEach(listener => listener(globalWeb3State));
};

export const useWeb3 = () => {
  // State for web3 connection
  const [provider, setProvider] = useState(globalWeb3State.provider);
  const [signer, setSigner] = useState(globalWeb3State.signer);
  const [contract, setContract] = useState(globalWeb3State.contract);
  const [governanceContract, setGovernanceContract] = useState(globalWeb3State.governanceContract);
  const [yildxTokenContract, setYildxTokenContract] = useState(globalWeb3State.yildxTokenContract);
  const [account, setAccount] = useState(globalWeb3State.account);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Effect to sync with global state on mount
  useEffect(() => {
    // Add a listener to update local state when global state changes
    const handleStateChange = (newState) => {
      setProvider(newState.provider);
      setSigner(newState.signer);
      setContract(newState.contract);
      setGovernanceContract(newState.governanceContract);
      setYildxTokenContract(newState.yildxTokenContract);
      setAccount(newState.account);
    };
    
    // Add this component as a listener
    listeners.push(handleStateChange);
    
    // Auto-connect if we have a persisted connection
    if (globalWeb3State.isConnected && !account) {
      // Check localStorage as a fallback
      autoConnect();
    }
    
    // Initial sync with global state
    handleStateChange(globalWeb3State);
    
    // Remove listener on unmount
    return () => {
      listeners = listeners.filter(listener => listener !== handleStateChange);
    };
  }, [account]);
  
  // Function to auto connect on page load/navigation
  const autoConnect = async () => {
    // Check if we have a saved account in localStorage
    const savedAccount = localStorage.getItem('connected_account');
    
    if (savedAccount && window.ethereum) {
      setIsLoading(true);
      
      try {
        // Try to get accounts from MetaMask without prompting
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        
        // If we have access to accounts and they match our saved account, reconnect
        if (accounts.length > 0 && accounts.some(acc => acc.toLowerCase() === savedAccount.toLowerCase())) {
          await connect();
        }
      } catch (error) {
        console.error("Error auto-connecting:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Connect wallet and initialize contracts
  const connect = async () => {
    setIsLoading(true);
    setError("");
    
    try {
      console.log("Connecting wallet and initializing contracts...");
      
      // Call web3Service to connect wallet and initialize contracts
      const { 
        provider: connectedProvider, 
        signer: connectedSigner, 
        contract: fundContract, 
        account: connectedAccount,
        governanceContract: gContract,
        yildxTokenContract: tokenContract
      } = await connectWallet();
      
      // Store all returned values in global state
      globalWeb3State = {
        provider: connectedProvider,
        signer: connectedSigner,
        contract: fundContract,
        governanceContract: gContract,
        yildxTokenContract: tokenContract,
        account: connectedAccount,
        isConnected: true
      };
      
      // Also save to local state
      setProvider(connectedProvider);
      setSigner(connectedSigner);
      setContract(fundContract);
      setAccount(connectedAccount);
      setGovernanceContract(gContract);
      setYildxTokenContract(tokenContract);
      
      // Save connected account to localStorage for persistence
      if (connectedAccount) {
        localStorage.setItem('connected_account', connectedAccount);
      }
      
      // Notify other components
      triggerListeners();
      
      // Log success for debugging
      console.log("Wallet connected successfully");
      console.log("Account:", connectedAccount);
      console.log("Fund contract available:", !!fundContract);
      console.log("Governance contract available:", !!gContract);
      console.log("YILDX token contract available:", !!tokenContract);
      
      return { success: true };
    } catch (error) {
      console.error("Wallet connection failed", error);
      setError("Failed to connect wallet. Please try again.");
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  // Disconnect wallet and clear state
  const disconnect = () => {
    // Clear global state
    globalWeb3State = {
      provider: null,
      signer: null,
      contract: null,
      governanceContract: null,
      yildxTokenContract: null,
      account: null,
      isConnected: false
    };
    
    // Clear local state
    setProvider(null);
    setSigner(null);
    setContract(null);
    setAccount(null);
    setGovernanceContract(null);
    setYildxTokenContract(null);
    
    // Remove from localStorage
    localStorage.removeItem('connected_account');
    
    // Notify other components
    triggerListeners();
    
    console.log("Wallet disconnected");
  };

  // Return all state and functions
  return {
    provider,
    signer,
    contract,
    governanceContract,
    yildxTokenContract,
    account,
    isLoading,
    error,
    connect,
    disconnect,
    setError
  };
};